import { FadeUp, LineReveal, Stagger, staggerItem } from "../components/Reveal";
import PageHeader from "../components/PageHeader";
import { BOARD, NORTH_AMERICA, EUROPE, ASIA } from "../data/leadership";
import { motion } from "framer-motion";

const VALUES = [
  { n: "01", title: "Integrity", body: "Uncompromising honesty across research, reporting, and partnership." },
  { n: "02", title: "Innovation", body: "Continual investment in ideas that expand the boundary of the possible." },
  { n: "03", title: "Precision", body: "The discipline of engineering exactness in every calculation and delivery." },
  { n: "04", title: "Scientific Excellence", body: "Peer-reviewed rigor at the foundation of every capability." },
  { n: "05", title: "Sustainability", body: "Environmental and social responsibility as engineering constraints." },
  { n: "06", title: "Global Collaboration", body: "Cross-border teams strengthened by cultural and institutional diversity." },
  { n: "07", title: "Safety", body: "A defense-in-depth culture that governs everything from labs to launch pads." },
];

function Story() {
  return (
    <section className="py-24 md:py-32" data-testid="about-story">
      <div className="max-w-[1440px] mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-10">
        <div className="md:col-span-3">
          <p className="eyebrow-gold">§ 01 — Company Story</p>
        </div>
        <div className="md:col-span-8 md:col-start-4 space-y-8 text-[var(--charcoal)] text-lg leading-relaxed">
          <FadeUp>
            <p className="first-letter:font-serif first-letter:text-7xl first-letter:float-left first-letter:leading-none first-letter:pr-4 first-letter:text-[var(--navy)]">
              Founded in Houston, Texas in 1968, Aurelius Dynamics began as a small consortium of nuclear physicists and aerospace engineers commissioned to solve a single generation of hard technical problems. In the decades that followed, the company grew into a multinational engineering and scientific organisation, quietly delivering critical programs for governments, defense agencies, and industrial partners across three continents.
            </p>
          </FadeUp>
          <FadeUp delay={0.2}>
            <p>
              Today Aurelius Dynamics operates specialised divisions in nuclear technology, space research, and strategic mineral resources. Our engineers design reactor systems, orbital platforms, and mineral processing infrastructure. Our scientists advance fundamental research in materials, propulsion, and radiation science. Our operators build the ethical, resilient supply chains that carry innovation into service.
            </p>
          </FadeUp>
          <FadeUp delay={0.3}>
            <p>
              The company remains privately held, guided by an independent board and led by a management team drawn from science, engineering, and public service. We are not the loudest company in any of our fields — but we are, by design, one of the most trusted.
            </p>
          </FadeUp>
        </div>
      </div>
    </section>
  );
}

function MissionVision() {
  return (
    <section className="py-16 md:py-24 bg-[var(--paper)]" data-testid="about-mission-vision">
      <div className="max-w-[1440px] mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-2 gap-px bg-[var(--line)] border border-[var(--line)]">
        <FadeUp className="bg-[var(--paper)] p-10 md:p-16">
          <p className="eyebrow-gold mb-8">§ 02 — Mission</p>
          <h2 className="font-serif text-4xl md:text-5xl text-[var(--navy)] leading-tight">
            To engineer scientifically excellent, socially responsible solutions to the world's most consequential technical challenges.
          </h2>
        </FadeUp>
        <FadeUp delay={0.15} className="bg-[var(--paper)] p-10 md:p-16">
          <p className="eyebrow-gold mb-8">§ 03 — Vision</p>
          <h2 className="font-serif text-4xl md:text-5xl text-[var(--navy)] leading-tight">
            <span className="italic">A century</span> of applied science serving the stability, prosperity, and safety of nations.
          </h2>
        </FadeUp>
      </div>
    </section>
  );
}

function Values() {
  return (
    <section className="py-24 md:py-32" data-testid="about-values">
      <div className="max-w-[1440px] mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-14">
          <div className="md:col-span-4"><p className="eyebrow-gold">§ 04 — Core Values</p></div>
          <div className="md:col-span-8">
            <FadeUp>
              <h2 className="h-section text-[clamp(2rem,5vw,4rem)] text-[var(--navy)]">
                Seven principles that <br /><span className="italic">govern our practice.</span>
              </h2>
            </FadeUp>
          </div>
        </div>
        <Stagger className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-px bg-[var(--line)] border border-[var(--line)]">
          {VALUES.map((v) => (
            <motion.div
              key={v.n}
              variants={staggerItem}
              className="bg-white p-8 min-h-[240px] flex flex-col justify-between group hover:bg-[var(--navy)] hover:text-white transition-colors duration-500"
              data-testid={`value-${v.n}`}
            >
              <p className="font-mono text-xs text-[var(--gold-soft)] group-hover:text-[var(--gold)]">{v.n}</p>
              <div>
                <h3 className="font-serif text-3xl text-[var(--navy)] group-hover:text-white mt-4">{v.title}</h3>
                <p className="mt-3 text-sm text-[var(--steel)] group-hover:text-white/70 leading-relaxed">{v.body}</p>
              </div>
            </motion.div>
          ))}
        </Stagger>
      </div>
    </section>
  );
}

function BoardCards() {
  return (
    <section className="py-24 md:py-32 bg-[var(--paper)]" data-testid="about-board">
      <div className="max-w-[1440px] mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-14">
          <div className="md:col-span-4"><p className="eyebrow-gold">§ 05 — Board of Directors</p></div>
          <div className="md:col-span-8">
            <FadeUp>
              <h2 className="h-section text-[clamp(2rem,5vw,4rem)] text-[var(--navy)]">
                Independent leadership.
              </h2>
            </FadeUp>
          </div>
        </div>
        <Stagger className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[var(--line)] border border-[var(--line)]">
          {BOARD.map((p, i) => (
            <motion.article
              key={p.name}
              variants={staggerItem}
              className="bg-white p-8 md:p-10 min-h-[280px] flex flex-col justify-between"
              data-testid={`board-card-${i}`}
            >
              <div>
                <p className="font-mono text-[10px] text-[var(--gold-soft)] tracking-[0.24em]">MEMBER · {String(i + 1).padStart(2, "0")}</p>
                <h3 className="mt-4 font-serif text-2xl text-[var(--navy)] leading-tight">{p.name}</h3>
                <p className="mt-2 text-sm uppercase tracking-[0.15em] text-[var(--steel)]">{p.role}</p>
                <p className="mt-5 text-sm text-[var(--charcoal)] leading-relaxed">{p.bio}</p>
              </div>
              <div className="mt-8 pt-5 border-t border-[var(--line)] flex justify-between text-xs">
                <span className="text-[var(--steel)] uppercase tracking-[0.14em]">{p.expertise}</span>
                <span className="font-mono text-[var(--gold-soft)]">{p.years} YRS</span>
              </div>
            </motion.article>
          ))}
        </Stagger>
      </div>
    </section>
  );
}

function RegionalTeam({ eyebrow, title, people, testid }) {
  return (
    <section className="py-16 md:py-24" data-testid={testid}>
      <div className="max-w-[1440px] mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-10 items-end">
          <div className="md:col-span-4">
            <p className="eyebrow-gold">{eyebrow}</p>
            <FadeUp><h2 className="mt-4 h-section text-4xl md:text-5xl text-[var(--navy)]">{title}</h2></FadeUp>
          </div>
          <div className="md:col-span-8 md:text-right">
            <span className="font-mono text-xs text-[var(--steel)] tracking-[0.2em]">{people.length.toString().padStart(2, "0")} EXECUTIVES</span>
          </div>
        </div>
        <div className="border-t border-[var(--line)]">
          {people.map((p, i) => (
            <FadeUp key={p.name} delay={i * 0.03}>
              <div className="grid grid-cols-12 gap-4 py-6 border-b border-[var(--line)] group hover:bg-[var(--paper)] transition-colors duration-300">
                <div className="col-span-1 font-mono text-xs text-[var(--gold-soft)] pt-1">{String(i + 1).padStart(2, "0")}</div>
                <div className="col-span-11 md:col-span-6">
                  <p className="font-serif text-2xl text-[var(--navy)]">{p.name}</p>
                </div>
                <div className="col-span-12 md:col-span-5 text-[var(--steel)] md:text-right md:pr-2 pt-1">{p.role}</div>
              </div>
            </FadeUp>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function About() {
  return (
    <div>
      <PageHeader
        eyebrow="§ About Us"
        title={["An engineering", "corporation with", "a scientific conscience."]}
        description="Founded in Houston in 1968, Aurelius Dynamics is a privately held multinational operating in nuclear technology, space research, and strategic minerals across North America, Europe, and Asia."
        meta={<span>Chapter I / IV</span>}
      />
      <Story />
      <MissionVision />
      <Values />
      <BoardCards />
      <RegionalTeam eyebrow="§ 06 — North America Leadership" title="North America." people={NORTH_AMERICA} testid="about-na" />
      <RegionalTeam eyebrow="§ 07 — Europe Leadership" title="Europe." people={EUROPE} testid="about-eu" />
      <RegionalTeam eyebrow="§ 08 — Asia Leadership" title="Asia." people={ASIA} testid="about-asia" />
    </div>
  );
}
